import { useCallback, useEffect, useRef, useState, type RefObject } from "react";
import { ModuleHeader } from "../../../../../shared/components/ModuleHeader";
import type { ApplyBrandResult, BrandSpec } from "../../../types";
import { useBrandExtractor, type ExtractorState } from "../hooks/useBrandExtractor";
import { useBrandApply, type ApplyState } from "../hooks/useBrandApply";
import { useMyBrandTemplates } from "../hooks/useMyBrandTemplates";
import { buildBrandSpecFromThemeColors } from "../services/brandExtractor";
import { normalizeHex } from "../../../utils/colourUtils";
import { deriveBrandedFilename, downloadActivePresentationCopy, goToSlide } from "../../../utils/officePptx";
import { loadFontData, type FontOption } from "../../FormatTools/services/fontData";
import { logOfficeError } from "../../../../../telemetry";
import { SavedTemplatesTab, type SavedTemplateSelectionStatus } from "./SavedTemplatesTab";
import type { SavedBrandTemplate } from "../services/brandTemplatesApi";
import type { MyBrandTemplate } from "../services/myBrandTemplatesStorage";
import { consumePendingSavedTemplateSelection } from "../services/pendingSavedTemplateSelection";
import styles from "./ApplyBrandPanel.module.css";

type Scope = "slide" | "deck";
type SourceTab = "active" | "upload" | "saved";
// Whole-Presentation-only: the apply itself always runs live against the
// open file (see ApplySuccessCard's doc comment) — this only decides
// whether the success card also offers to download a separate branded copy.
type SaveTarget = "new" | "current";

export interface AdvancedOpts {
  allowShadows: boolean;
  allowGradients: boolean;
  allowRoundedCorners: boolean;
  allowAnimations: boolean;
  compressImages: boolean;
  fixAlignment: boolean;
  autofitSlideContent: boolean;
}

// The four "Allow …" visual-effect toggles default ON (opt-out — matches
// brandApplier's `removeShadows: options.allowShadows === false` reading,
// where undefined/true both mean "keep"). The remaining three are opt-in:
// compressing images, repositioning shapes, and forcing autofit are all
// more invasive/slower operations the mockup marks off by default.
const DEFAULT_ADVANCED_OPTIONS: AdvancedOpts = {
  allowShadows: true, allowGradients: true, allowRoundedCorners: true, allowAnimations: true,
  compressImages: false, fixAlignment: false, autofitSlideContent: false,
};

const SCOPE_NOTE: Record<Scope, string> = {
  slide: "The brand is applied to the current slide only. The rest of the presentation is unchanged.",
  deck: "This presentation will be updated with the new brand.",
};

const SAVE_TARGET_NOTE: Record<SaveTarget, string> = {
  new: "This presentation is updated directly. You can also save a separate branded copy after applying.",
  current: "This presentation is updated directly with the new brand.",
};

type DownloadState = "idle" | "saving" | "done";

const DOWNLOAD_STATE_LABEL: Record<DownloadState, string> = {
  idle: "Open the New File",
  saving: "Saving…",
  done: "Downloaded ✓",
};

interface ApplyBrandPanelProps {
  readonly onBack?: () => void;
}

export function ApplyBrandPanel({ onBack }: Readonly<ApplyBrandPanelProps>) {
  const [sourceTab, setSourceTab] = useState<SourceTab>("active");
  const [scope, setScope] = useState<Scope>("slide");
  const [saveTarget, setSaveTarget] = useState<SaveTarget>("new");
  const [advanced, setAdvanced] = useState<AdvancedOpts>(DEFAULT_ADVANCED_OPTIONS);
  // Edits committed from the Review & Customise screen (This Deck's Theme
  // flow only) — null until the user opens Review & Customise and hits Done.
  const [editedBrand, setEditedBrand] = useState<BrandSpec | null>(null);
  const [reviewing, setReviewing] = useState(false);
  // Client-side file validation error — rendered via the same statusBox
  // styling as an extraction failure, instead of a native alert(), so both
  // failure modes on this step look uniform to the user.
  const [fileError, setFileError] = useState<string | null>(null);
  // Which tab the current extraction belongs to — lets the tab bar stay
  // visible at all times (no separate "back" step) while still only ever
  // showing one ready source at once: switching to the other tab discards
  // whatever the current tab extracted, exactly like the old back button did.
  const [extractedFor, setExtractedFor] = useState<SourceTab | null>(null);
  // Which saved template (if any) is selected, from either the org-managed
  // list or the user's own My Templates list — kept separate from
  // extractedFor/brand so the row stays highlighted while its extraction runs.
  const [savedSelection, setSavedSelection] = useState<
    { kind: "org"; template: SavedBrandTemplate } | { kind: "local"; template: MyBrandTemplate } | null
  >(null);
  // Whether the current Upload extraction has already been saved to My
  // Templates — resets whenever a new file is uploaded/discarded.
  const [uploadSaved, setUploadSaved] = useState(false);

  const { state: extractState, extract, extractFromActive, extractFromUrls, setDirectBrand, reset: resetExtract } = useBrandExtractor();
  const { state: applyState, apply, reset: resetApply } = useBrandApply();
  const { templates: myTemplates, toast: myTemplateToast, save: saveMyTemplate, remove: removeMyTemplate } = useMyBrandTemplates();

  const handleFiles = useCallback((files: FileList | null) => {
    const file = files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".pptx")) {
      setFileError("Please select a .pptx template file.");
      return;
    }
    setFileError(null);
    resetApply();
    setExtractedFor("upload");
    setUploadSaved(false);
    // Auto-advances to preview once extraction succeeds; the hook traps its
    // own errors into state, so this rejection handler is a lint-level guard —
    // it should never fire, but is still traced rather than swallowed.
    extract(file).catch((e) => logOfficeError(e, "apply-brand-extract-upload"));
  }, [extract, resetApply]);

  const handleExtractActive = useCallback(() => {
    setExtractedFor("active");
    extractFromActive().catch((e) => logOfficeError(e, "apply-brand-extract-active"));
  }, [extractFromActive]);

  const handleSelectSavedTemplate = useCallback((template: SavedBrandTemplate) => {
    // Prefer building the brand synchronously from themeColors; only fall
    // back to downloading + parsing the .pptx when there's no themeColors.
    if (!template.themeColors && template.sourceFileUrlCandidates.length === 0) return;
    setSavedSelection({ kind: "org", template });
    setExtractedFor("saved");
    if (template.themeColors) {
      setDirectBrand(buildBrandSpecFromThemeColors(template.themeColors), template.name);
    } else {
      extractFromUrls(template.sourceFileUrlCandidates, template.name).catch((e) => logOfficeError(e, "apply-brand-extract-saved"));
    }
  }, [extractFromUrls, setDirectBrand]);

  const handleSelectMyTemplate = useCallback((template: MyBrandTemplate) => {
    setSavedSelection({ kind: "local", template });
    setExtractedFor("saved");
    setDirectBrand(template.brand, template.name);
  }, [setDirectBrand]);

  const handleRemoveMyTemplate = useCallback((id: string) => {
    removeMyTemplate(id);
    if (savedSelection?.kind === "local" && savedSelection.template.id === id) {
      resetExtract();
      resetApply();
      setEditedBrand(null);
      setExtractedFor(null);
      setSavedSelection(null);
    }
  }, [removeMyTemplate, savedSelection, resetExtract, resetApply]);

  const handleSaveUploadTemplate = useCallback((brand: BrandSpec, uploadFilename: string) => {
    if (saveMyTemplate(uploadFilename, brand, brandSwatches(brand))) setUploadSaved(true);
  }, [saveMyTemplate]);

  // The Asset Library's Brand Templates tab routes a pick here instead of
  // doing anything itself — see pendingSavedTemplateSelection.ts. Runs once,
  // on mount only: a later, ordinary visit to Apply Brand must not replay a
  // stale pick, which is exactly what consuming (not just reading) achieves.
  useEffect(() => {
    const pending = consumePendingSavedTemplateSelection();
    if (!pending) return;
    setSourceTab("saved");
    if (pending.kind === "org") handleSelectSavedTemplate(pending.template);
    else handleSelectMyTemplate(pending.template);
  }, [handleSelectSavedTemplate, handleSelectMyTemplate]);

  // The view is driven by extraction state: no brand yet → source selection,
  // brand extracted → preview + apply. Review & Customise edits (Theme tab
  // only) override the raw extracted spec for the actual apply call.
  const brand: BrandSpec | null = extractState.status === "done" ? extractState.brand : null;
  const filename = extractState.status === "done" ? extractState.filename : "";
  const finalBrand = editedBrand ?? brand;

  const handleApply = useCallback(() => {
    if (!finalBrand) return;
    resetApply();
    // Same lint-level guard as handleExtractActive above — apply() traps its
    // own errors into applyState.
    apply(finalBrand, { scope, ...advanced }).catch((e) => logOfficeError(e, "apply-brand-apply"));
  }, [finalBrand, scope, advanced, apply, resetApply]);

  const handleTabChange = useCallback((tab: SourceTab) => {
    setFileError(null);
    setSourceTab(tab);
    // Switching to the tab that doesn't own the current extraction discards
    // it — only one source can be "ready" at a time, same as the old back
    // button, just triggered from the (now always-visible) tab bar instead.
    if (extractedFor !== null && extractedFor !== tab) {
      resetExtract();
      resetApply();
      setEditedBrand(null);
      setReviewing(false);
      setAdvanced(DEFAULT_ADVANCED_OPTIONS);
      setExtractedFor(null);
      setSavedSelection(null);
      setUploadSaved(false);
    }
  }, [extractedFor, resetExtract, resetApply]);

  const isBusy = extractState.status === "extracting" || applyState.status === "applying";
  const applyDisabled = !finalBrand || isBusy;

  if (reviewing && brand) {
    return (
      <ReviewCustomisePanel
        brand={editedBrand ?? brand}
        advanced={advanced}
        onDone={(nextBrand, nextAdvanced) => {
          setEditedBrand(nextBrand);
          setAdvanced(nextAdvanced);
          setReviewing(false);
        }}
        onCancel={() => setReviewing(false)}
      />
    );
  }

  return (
    <div className={styles.wrapper}>
      <div className={styles.scrollArea}>
        <ModuleHeader title="Apply Brand" subtitle="Deckorate" onBack={onBack} />

        <SourceStep
          sourceTab={sourceTab}
          onTabChange={handleTabChange}
          extractState={extractState}
          fileError={fileError}
          onExtractActive={handleExtractActive}
          onFiles={handleFiles}
          brand={brand}
          editedBrand={editedBrand}
          filename={filename}
          extractedFor={extractedFor}
          onReview={() => setReviewing(true)}
          onSelectSavedTemplate={handleSelectSavedTemplate}
          myTemplates={myTemplates}
          onSelectMyTemplate={handleSelectMyTemplate}
          onRemoveMyTemplate={handleRemoveMyTemplate}
          selectedId={savedSelection?.template.id ?? null}
          uploadSaved={uploadSaved}
          onSaveUploadTemplate={handleSaveUploadTemplate}
        />

        {myTemplateToast && <SavedTemplateToast message={myTemplateToast} />}

        {/* Apply-to section — flows inline below the ready card/Review &
           Customise instead of docking to the bottom of the panel. */}
        <div className={styles.applyToSection}>
          <div className={styles.actionBarLabel}>APPLY TO</div>
          <div className={styles.seg}>
            <button
              type="button"
              className={`${styles.segBtn} ${scope === "deck" ? styles.segBtnActive : ""}`}
              data-cmd-action="brand-apply-scope-deck"
              onClick={() => setScope("deck")}
            >
              Whole Presentation
            </button>
            <button
              type="button"
              className={`${styles.segBtn} ${scope === "slide" ? styles.segBtnActive : ""}`}
              data-cmd-action="brand-apply-scope-slide"
              onClick={() => setScope("slide")}
            >
              Just This Slide
            </button>
          </div>
          <div className={styles.scopeNote}>
            <svg aria-hidden="true" viewBox="0 0 20 20" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className={styles.scopeNoteIcon}>
              <circle cx="10" cy="10" r="7.5" />
              <path d="M10 9v4M10 6.5v.5" />
            </svg>
            <span>{SCOPE_NOTE[scope]}</span>
          </div>

          {scope === "deck" && (
            <>
              <div className={styles.actionBarLabel}>SAVE RESULT TO</div>
              <div className={styles.seg}>
                <button
                  type="button"
                  className={`${styles.segBtn} ${saveTarget === "new" ? styles.segBtnActive : ""}`}
                  onClick={() => setSaveTarget("new")}
                >
                  New Presentation
                </button>
                <button
                  type="button"
                  className={`${styles.segBtn} ${saveTarget === "current" ? styles.segBtnActive : ""}`}
                  onClick={() => setSaveTarget("current")}
                >
                  This Presentation
                </button>
              </div>
              <div className={styles.scopeNote}>
                <svg aria-hidden="true" viewBox="0 0 20 20" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className={styles.scopeNoteIcon}>
                  <circle cx="10" cy="10" r="7.5" />
                  <path d="M10 9v4M10 6.5v.5" />
                </svg>
                <span>{SAVE_TARGET_NOTE[saveTarget]}</span>
              </div>
            </>
          )}

          <ApplyStatus applyState={applyState} scope={scope} saveTarget={saveTarget} />

          <button
            type="button"
            className={styles.applyBtn}
            data-cmd-action="brand-apply-confirm"
            onClick={handleApply}
            disabled={applyDisabled}
          >
            {isBusy ? "Applying…" : "Apply Brand"}
          </button>
          {!finalBrand && <div className={styles.applyHint}>Extract or upload a brand above to continue.</div>}
        </div>
      </div>
    </div>
  );
}

// ─── Sub-components ───────────────────────────────────────────────────────────

/** The tab bar stays visible at all times — no separate "back" step. Each
 * tab shows either its picker (theme card / upload dropzone) or, once its
 * own extraction is the one currently "ready" (`extractedFor === sourceTab`),
 * the shared ready card + Review & Customise entry point. Switching to the
 * other tab discards whichever extraction was ready (see handleTabChange). */
interface SourceStepProps {
  sourceTab: SourceTab;
  onTabChange: (tab: SourceTab) => void;
  extractState: ExtractorState;
  fileError: string | null;
  onExtractActive: () => void;
  onFiles: (files: FileList | null) => void;
  brand: BrandSpec | null;
  editedBrand: BrandSpec | null;
  filename: string;
  extractedFor: SourceTab | null;
  onReview: () => void;
  onSelectSavedTemplate: (template: SavedBrandTemplate) => void;
  myTemplates: readonly MyBrandTemplate[];
  onSelectMyTemplate: (template: MyBrandTemplate) => void;
  onRemoveMyTemplate: (id: string) => void;
  selectedId: string | null;
  uploadSaved: boolean;
  onSaveUploadTemplate: (brand: BrandSpec, filename: string) => void;
}

// So the selected row's button can show "Extraction failed" instead of being
// stuck on "Extracting…" forever.
function getSavedTemplateStatus(
  extractedFor: SourceTab | null,
  extractState: ExtractorState,
  brand: BrandSpec | null,
): SavedTemplateSelectionStatus {
  if (extractedFor !== "saved") return "idle";
  if (extractState.status === "extracting") return "extracting";
  if (extractState.status === "done" && brand) return "ready";
  if (extractState.status === "error") return "error";
  return "idle";
}

function ActiveThemePicker({ onExtractActive, isBusyExtract }: Readonly<{ onExtractActive: () => void; isBusyExtract: boolean }>) {
  return (
    <button type="button" className={styles.themeCard} onClick={onExtractActive} disabled={isBusyExtract}>
      <span className={styles.themeCardDot} aria-hidden="true" />
      <span className={styles.themeCardText}>
        <span className={styles.themeCardTitle}>{isBusyExtract ? "Reading this deck…" : "Use this deck’s theme"}</span>
        <span className={styles.themeCardDesc}>
          Make every slide match the look already in this file. No template needed.
        </span>
      </span>
    </button>
  );
}

function UploadDropzone({ fileInputRef, onFiles }: Readonly<{
  fileInputRef: RefObject<HTMLInputElement>;
  onFiles: (files: FileList | null) => void;
}>) {
  return (
    <div className={styles.sourcePane}>
      <p className={styles.sourceDesc}>
        Upload a PPTX template to extract its brand colours and fonts.
      </p>
      <input
        ref={fileInputRef}
        type="file"
        accept=".pptx,application/vnd.openxmlformats-officedocument.presentationml.presentation"
        aria-label="Upload brand template PPTX file"
        className={styles.uploadInput}
        onChange={(e) => onFiles(e.target.files)}
      />
      <button
        type="button"
        className={styles.uploadZone}
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => { e.preventDefault(); onFiles(e.dataTransfer.files); }}
        onClick={() => fileInputRef.current?.click()}
      >
        <span className={styles.uploadIcon} aria-hidden="true">📄</span>
        <span className={styles.uploadLabel}>Drop a PPTX template here</span>
        <span className={styles.uploadHint}>or click to browse</span>
      </button>
    </div>
  );
}

function renderSourceContent(
  props: SourceStepProps,
  ready: boolean,
  isBusyExtract: boolean,
  fileInputRef: RefObject<HTMLInputElement>,
): JSX.Element {
  const {
    sourceTab, brand, editedBrand, filename, onExtractActive, onReview, extractedFor, extractState,
    selectedId, onSelectSavedTemplate, myTemplates, onSelectMyTemplate, onRemoveMyTemplate,
    uploadSaved, onSaveUploadTemplate, onFiles,
  } = props;

  if (sourceTab === "saved") {
    return (
      <SavedTemplatesTab
        selectedId={selectedId}
        disabled={isBusyExtract}
        selectedStatus={getSavedTemplateStatus(extractedFor, extractState, brand)}
        onSelect={onSelectSavedTemplate}
        onReview={onReview}
        myTemplates={myTemplates}
        onSelectMyTemplate={onSelectMyTemplate}
        onRemoveMyTemplate={onRemoveMyTemplate}
      />
    );
  }

  if (sourceTab === "active") {
    return ready && brand ? (
      <ThemeReadyCard
        brand={editedBrand ?? brand}
        filename={filename}
        title="Use this deck’s theme"
        description="Make every slide match the look already in this file. No template needed."
        onReview={onReview}
      />
    ) : (
      <ActiveThemePicker onExtractActive={onExtractActive} isBusyExtract={isBusyExtract} />
    );
  }

  return ready && brand ? (
    <UploadReadyCard
      brand={editedBrand ?? brand}
      filename={filename}
      onReview={onReview}
      saved={uploadSaved}
      onSave={() => onSaveUploadTemplate(editedBrand ?? brand, filename)}
    />
  ) : (
    <UploadDropzone fileInputRef={fileInputRef} onFiles={onFiles} />
  );
}

function SourceStep(props: Readonly<SourceStepProps>) {
  const { sourceTab, onTabChange, extractState, fileError, brand, extractedFor } = props;
  const fileInputRef = useRef<HTMLInputElement>(null);
  const isBusyExtract = extractState.status === "extracting";
  const ready = brand !== null && extractedFor === sourceTab;
  const sourceContent = renderSourceContent(props, ready, isBusyExtract, fileInputRef);

  return (
    <>
      <div className={styles.sourceTabs}>
        <button
          type="button"
          className={`${styles.sourceTabBtn} ${sourceTab === "active" ? styles.sourceTabBtnActive : ""}`}
          onClick={() => onTabChange("active")}
        >
          This Deck&rsquo;s Theme
        </button>
        <button
          type="button"
          className={`${styles.sourceTabBtn} ${sourceTab === "upload" ? styles.sourceTabBtnActive : ""}`}
          onClick={() => onTabChange("upload")}
        >
          Upload
        </button>
        <button
          type="button"
          className={`${styles.sourceTabBtn} ${sourceTab === "saved" ? styles.sourceTabBtnActive : ""}`}
          onClick={() => onTabChange("saved")}
        >
          Saved Templates
        </button>
      </div>

      {sourceContent}

      {fileError && (
        <div className={`${styles.statusBox} ${styles.statusError}`}>
          <span className={styles.statusLabel}>Invalid file:</span>
          {fileError}
        </div>
      )}
      {/* Saved Templates shows its own inline extracting state — see SavedTemplatesTab. */}
      {extractState.status === "extracting" && sourceTab !== "saved" && (
        sourceTab === "upload" ? (
          <ExtractingChecklist filename={extractState.filename} />
        ) : (
          <div className={`${styles.statusBox} ${styles.statusExtracting}`}>
            <span className={styles.statusLabel}>Extracting brand…</span>
            {extractState.filename}
          </div>
        )
      )}
      {extractState.status === "error" && (
        <div className={`${styles.statusBox} ${styles.statusError}`}>
          <span className={styles.statusLabel}>Extraction failed:</span>
          {extractState.message}
        </div>
      )}
    </>
  );
}

/** Short-lived confirmation shown after saving an uploaded template to My
 *  Templates — same fixed-bottom treatment as Share & Export's toast. */
function SavedTemplateToast({ message }: Readonly<{ message: string }>) {
  return (
    <output className={styles.toast} aria-live="polite">
      <span className={styles.toastIcon} aria-hidden="true">✓</span>
      {message}
    </output>
  );
}

function ApplyStatus({ applyState, scope, saveTarget }: Readonly<{ applyState: ApplyState; scope: Scope; saveTarget: SaveTarget }>) {
  if (applyState.status === "applying") {
    const rawPct = applyState.total > 0 ? (applyState.current / applyState.total) * 100 : 0;
    const progressPct = Math.min(100, Math.max(0, Math.round(rawPct)));
    return (
      <>
        <div className={styles.progressBar}>
          <div className={styles.progressFill} style={{ width: `${progressPct}%` }} />
        </div>
        <p className={styles.progressMsg}>{applyState.message}</p>
      </>
    );
  }

  if (applyState.status === "done") {
    return <ApplySuccessCard result={applyState.result} scope={scope} saveTarget={saveTarget} />;
  }

  if (applyState.status === "error") {
    return (
      <div className={`${styles.resultBox} ${styles.resultError}`}>
        <span className={styles.resultLabel}>Failed:</span>
        {applyState.message}
      </div>
    );
  }

  return null;
}

/** The success state's card — dismissible, always with a "View report" link
 * that opens the same action list in a focused modal. The other footer
 * action depends on scope/saveTarget:
 * - Just This Slide, or Whole Presentation + "This Presentation": "View the
 *   Slides", which navigates PowerPoint back to the affected slide —
 *   `result.changedSlideIndex` for the single-slide case (in case the user
 *   navigated elsewhere since applying), or slide 1 for the whole deck (a
 *   defined start point across every changed slide).
 * - Whole Presentation + "New Presentation": the apply already ran live
 *   against the open file (brandApplier only knows how to mutate the live
 *   document — see the module-level note on SaveTarget), so "Open the New
 *   File" additionally exports those same bytes as a separate downloaded
 *   copy on demand, rather than assuming the user wants a download on every
 *   apply. */
function ApplySuccessCard({ result, scope, saveTarget }: Readonly<{ result: ApplyBrandResult; scope: Scope; saveTarget: SaveTarget }>) {
  const [dismissed, setDismissed] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [downloadState, setDownloadState] = useState<DownloadState>("idle");

  const isNewCopy = scope === "deck" && saveTarget === "new";

  // A fresh apply always produces a new `result` object, so resetting on
  // that reference re-opens the card (and closes any stale report/download
  // confirmation) for the next apply instead of carrying over stale state.
  useEffect(() => {
    setDismissed(false);
    setReportOpen(false);
    setDownloadState("idle");
  }, [result]);

  const handleViewSlides = useCallback(() => {
    setDismissed(true);
    const target = scope === "deck" ? 1 : result.changedSlideIndex;
    if (target) goToSlide(target).catch((e) => logOfficeError(e, "apply-brand-view-slides"));
  }, [scope, result.changedSlideIndex]);

  const handleOpenNewFile = useCallback(() => {
    setDownloadState("saving");
    downloadActivePresentationCopy(deriveBrandedFilename())
      .then(() => setDownloadState("done"))
      .catch((e) => {
        logOfficeError(e, "apply-brand-download-copy");
        setDownloadState("idle");
      });
  }, []);

  if (dismissed) return null;

  return (
    <>
      <div className={`${styles.resultBox} ${styles.resultSuccess}`}>
        <div className={styles.resultHeader}>
          <span className={styles.resultHeaderTitle}>
            <span aria-hidden="true">✓</span> {isNewCopy ? "Branded copy ready" : "Brand applied"}
          </span>
          <button type="button" className={styles.resultDismiss} aria-label="Dismiss" onClick={() => setDismissed(true)}>
            ×
          </button>
        </div>
        <p className={styles.resultCaption}>
          {isNewCopy
            ? "This presentation was updated directly. Click “Open the New File” to also save a separate branded copy."
            : "This file was updated with the new brand."}
        </p>
        {result.actions.length > 0 && (
          <ul className={styles.resultActions}>
            {result.actions.map((a) => <li key={a}>{a}</li>)}
          </ul>
        )}
        <div className={styles.resultFooter}>
          {isNewCopy ? (
            <button
              type="button"
              className={styles.viewSlidesBtn}
              onClick={handleOpenNewFile}
              disabled={downloadState === "saving"}
            >
              {DOWNLOAD_STATE_LABEL[downloadState]}
            </button>
          ) : (
            <button type="button" className={styles.viewSlidesBtn} onClick={handleViewSlides}>
              View the Slides
            </button>
          )}
          <button type="button" className={styles.viewReportLink} onClick={() => setReportOpen(true)}>
            View report
          </button>
        </div>
      </div>
      {reportOpen && <BrandReportModal result={result} onClose={() => setReportOpen(false)} />}
    </>
  );
}

function BrandReportModal({ result, onClose }: Readonly<{ result: ApplyBrandResult; onClose: () => void }>) {
  const dialogRef = useRef<HTMLDialogElement>(null);

  // Native <dialog> owns focus trapping, Escape-to-close and the modal a11y
  // tree for free — the "close" event (fired for both Escape and
  // dialog.close()) is the single place we sync back to React state. Some
  // older Office WebView hosts (and jsdom under test) don't implement
  // showModal()/close(), so fall back to the plain `open` attribute there.
  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (typeof dialog.showModal === "function") {
      dialog.showModal();
    } else {
      dialog.setAttribute("open", "");
    }
    dialog.addEventListener("close", onClose);
    return () => dialog.removeEventListener("close", onClose);
  }, [onClose]);

  const closeDialog = useCallback(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (typeof dialog.close === "function") {
      dialog.close();
    } else {
      dialog.removeAttribute("open");
      onClose();
    }
  }, [onClose]);

  return (
    <dialog
      ref={dialogRef}
      className={styles.reportModal}
      aria-label="Brand Application Report"
    >
      <div className={styles.reportHeader}>
        <span className={styles.reportTitle}>Brand Application Report</span>
        <button type="button" className={styles.resultDismiss} aria-label="Close report" onClick={closeDialog}>
          ×
        </button>
      </div>
      <p className={styles.reportSummary}>
        {result.slidesProcessed} slide(s) processed, {result.shapesUpdated} shape(s) updated.
      </p>
      {result.actions.length > 0 ? (
        <ul className={styles.reportList}>
          {result.actions.map((a) => <li key={a}>{a}</li>)}
        </ul>
      ) : (
        <p className={styles.reportEmpty}>No changes were needed — this file already matched the brand.</p>
      )}
      <button type="button" className={styles.applyBtn} onClick={closeDialog}>
        Got it
      </button>
    </dialog>
  );
}

/** Reading-progress checklist shown while an uploaded template is being
 * parsed — the extractor itself is a single async call with no granular
 * phase signal, so these steps advance on a timer purely to give the user
 * a sense of progress; they all resolve to "done" once extraction finishes. */
const EXTRACT_STEPS = [
  "Opening the file",
  "Finding theme colours",
  "Reading fonts",
  "Checking table styles",
  "Building a preview",
];

function ExtractingChecklist({ filename }: Readonly<{ filename: string }>) {
  const [stepIndex, setStepIndex] = useState(0);

  useEffect(() => {
    setStepIndex(0);
    const id = setInterval(() => {
      setStepIndex((i) => (i < EXTRACT_STEPS.length - 1 ? i + 1 : i));
    }, 550);
    return () => clearInterval(id);
  }, [filename]);

  return (
    <div className={styles.extractChecklist}>
      <div className={styles.extractChecklistTitle}>Reading {filename}…</div>
      <ul className={styles.extractChecklistList}>
        {EXTRACT_STEPS.map((label, i) => {
          const done = i < stepIndex;
          const active = i === stepIndex;
          let icon = "○";
          if (done) icon = "✓";
          else if (active) icon = "●";
          return (
            <li
              key={label}
              className={[
                styles.extractChecklistItem,
                done ? styles.extractChecklistDone : "",
                active ? styles.extractChecklistActive : "",
              ].filter(Boolean).join(" ")}
            >
              <span className={styles.extractChecklistIcon} aria-hidden="true">
                {icon}
              </span>
              {label}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/** The ready state shared by both source flows (This Deck's Theme + Upload):
 * selected card + swatches + the "will be applied" note + a Review &
 * Customise entry point. `title`/`description` let each flow supply its own
 * copy while reusing the same visual treatment and Review & Customise step. */
function ThemeReadyCard({ brand, filename, title, description, onReview }: Readonly<{
  brand: BrandSpec; filename: string; title: string; description: string; onReview: () => void;
}>) {
  const swatches = brandSwatches(brand);
  return (
    <>
      <div className={`${styles.themeCard} ${styles.themeCardSelected}`}>
        <span className={`${styles.themeCardDot} ${styles.themeCardDotOn}`} aria-hidden="true" />
        <span className={styles.themeCardText}>
          <span className={styles.themeCardTitleRow}>
            <span className={styles.themeCardTitle}>{title}</span>
            {swatches.length > 0 && (
              <span className={styles.themeCardSwatches}>
                {swatches.map((hex) => (
                  <span key={hex} className={styles.themeCardSwatch} style={{ backgroundColor: `#${hex}` }} title={`#${hex}`} />
                ))}
              </span>
            )}
          </span>
          <span className={styles.themeCardDesc}>{description}</span>
        </span>
      </div>
      <div className={styles.selectedNote}>
        <b>{filename}</b> will be applied with its colours, fonts and styles.
        <div>
          <button type="button" className={styles.reviewBtn} onClick={onReview}>
            Review &amp; Customise
          </button>
        </div>
      </div>
    </>
  );
}

/** The Upload flow's ready state: a "this session's uploads" card with a
 * Save action that persists the extracted brand to My Templates (see
 * useMyBrandTemplates), a "found in the file" summary of what was actually
 * extracted, and the same "will be applied" note + Review & Customise entry
 * point as the This Deck's Theme flow. */
function UploadReadyCard({ brand, filename, onReview, saved, onSave }: Readonly<{
  brand: BrandSpec; filename: string; onReview: () => void; saved: boolean; onSave: () => void;
}>) {
  const swatches = brandSwatches(brand);
  const fontCount = new Set([brand.fonts.titleFont, brand.fonts.bodyFont].filter(Boolean)).size;
  const tableNote = brand.tableDetected
    ? "Table styles matched from the template."
    : "Table — no sample found, theme defaults used.";

  return (
    <>
      <div className={styles.sessionLabel}>This session&rsquo;s uploads</div>
      <div className={styles.sessionCard}>
        <span className={`${styles.themeCardDot} ${styles.themeCardDotOn}`} aria-hidden="true" />
        <span className={styles.sessionCardMain}>
          <span className={styles.sessionCardName}>{filename}</span>
          {swatches.length > 0 && (
            <span className={styles.themeCardSwatches}>
              {swatches.map((hex) => (
                <span key={hex} className={styles.themeCardSwatch} style={{ backgroundColor: `#${hex}` }} title={`#${hex}`} />
              ))}
            </span>
          )}
        </span>
        {saved ? (
          <span className={styles.sessionCardSaved}>Saved ✓</span>
        ) : (
          <button type="button" className={styles.sessionCardSaveBtn} onClick={onSave}>
            Save
          </button>
        )}
      </div>

      <div className={styles.foundBanner}>
        <b>Found in {filename}:</b> {swatches.length} theme colour{swatches.length === 1 ? "" : "s"},{" "}
        {fontCount} font{fontCount === 1 ? "" : "s"}. {tableNote} Change any value in Review &amp; Customise.
      </div>

      <div className={styles.selectedNote}>
        <b>{filename}</b> will be applied with its colours, fonts and styles.
        <div>
          <button type="button" className={styles.reviewBtn} onClick={onReview}>
            Review &amp; Customise
          </button>
        </div>
      </div>
    </>
  );
}

function brandSwatches(brand: BrandSpec): string[] {
  return [
    brand.colors.accent1, brand.colors.accent2, brand.colors.accent3,
    brand.colors.accent4, brand.colors.accent5, brand.colors.accent6,
    brand.colors.dk1, brand.colors.dk2,
  ].map((c) => normalizeHex(c)).filter((c): c is string => c !== null);
}

// ─── Advanced Options (shared by the Upload inline block and Review & Customise) ──

interface ToggleSpec {
  key: keyof AdvancedOpts;
  label: string;
  hint?: string;
}

const VISUAL_EFFECTS: ToggleSpec[] = [
  { key: "allowRoundedCorners", label: "Allow rounded corners" },
  { key: "allowShadows", label: "Allow shadows" },
  { key: "allowGradients", label: "Allow gradient colours" },
  { key: "allowAnimations", label: "Allow animations" },
];
const IMAGE_HANDLING: ToggleSpec[] = [
  { key: "compressImages", label: "Compress images" },
];
const LAYOUT_ADJUSTMENTS: ToggleSpec[] = [
  { key: "fixAlignment", label: "Fix alignment", hint: "slower" },
  { key: "autofitSlideContent", label: "Autofit slide content" },
];

function AdvancedOptionsFields({ advanced, onChange }: Readonly<{ advanced: AdvancedOpts; onChange: (next: AdvancedOpts) => void }>) {
  const set = (key: keyof AdvancedOpts, value: boolean) => onChange({ ...advanced, [key]: value });
  return (
    <>
      <AdvancedGroup title="VISUAL EFFECTS" specs={VISUAL_EFFECTS} advanced={advanced} onSet={set} />
      <AdvancedGroup title="IMAGE HANDLING" specs={IMAGE_HANDLING} advanced={advanced} onSet={set} />
      <AdvancedGroup title="LAYOUT ADJUSTMENTS" specs={LAYOUT_ADJUSTMENTS} advanced={advanced} onSet={set} />
    </>
  );
}

function AdvancedGroup({ title, specs, advanced, onSet }: Readonly<{
  title: string;
  specs: ToggleSpec[];
  advanced: AdvancedOpts;
  onSet: (key: keyof AdvancedOpts, value: boolean) => void;
}>) {
  return (
    <div className={styles.advGroup}>
      <div className={styles.advGroupLabel}>{title}</div>
      {specs.map(({ key, label, hint }) => (
        <AdvCheck key={key} label={label} hint={hint} checked={advanced[key]} onChange={(v) => onSet(key, v)} />
      ))}
    </div>
  );
}

function AdvCheck({ label, hint, checked, onChange }: Readonly<{ label: string; hint?: string; checked: boolean; onChange: (v: boolean) => void }>) {
  return (
    <label className={styles.toggleRow}>
      <span>
        {label}
        {hint && <span className={styles.toggleHint}> {hint}</span>}
      </span>
      <input
        type="checkbox"
        role="switch"
        className={styles.toggleInput}
        checked={checked}
        aria-label={hint ? `${label} ${hint}` : undefined}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span className={styles.toggleTrack} aria-hidden="true">
        <span className={styles.toggleThumb} />
      </span>
    </label>
  );
}

// ─── Review & Customise (This Deck's Theme flow) ──────────────────────────────

type ColorKey = keyof BrandSpec["colors"];

const COLOR_FIELDS: Array<{ key: ColorKey; label: string }> = [
  { key: "dk1", label: "Dark 1" }, { key: "lt1", label: "Light 1" },
  { key: "dk2", label: "Dark 2" }, { key: "lt2", label: "Light 2" },
  { key: "accent1", label: "Accent 1" }, { key: "accent2", label: "Accent 2" },
  { key: "accent3", label: "Accent 3" }, { key: "accent4", label: "Accent 4" },
  { key: "accent5", label: "Accent 5" }, { key: "accent6", label: "Accent 6" },
  { key: "hlink", label: "Link" }, { key: "folHlink", label: "Visited" },
];

/** `<input type="color">` requires a lowercase #rrggbb value — falls back to
 * a neutral grey for anything the extractor didn't populate. */
function toColorInputValue(hex: string | null | undefined): string {
  const norm = normalizeHex(hex ?? undefined);
  return norm ? `#${norm.toLowerCase()}` : "#808080";
}

interface ReviewCustomisePanelProps {
  readonly brand: BrandSpec;
  readonly advanced: AdvancedOpts;
  readonly onDone: (brand: BrandSpec, advanced: AdvancedOpts) => void;
  readonly onCancel: () => void;
}

function ReviewCustomisePanel({ brand, advanced, onDone, onCancel }: ReviewCustomisePanelProps) {
  // Local working copy — edits are discarded on cancel/back, only committed
  // via Done, matching the mockup's "save your changes before leaving" note.
  const [draft, setDraft] = useState<BrandSpec>(brand);
  const [draftAdv, setDraftAdv] = useState<AdvancedOpts>(advanced);
  const [fonts, setFonts] = useState<FontOption[]>([]);

  useEffect(() => {
    let live = true;
    loadFontData().then((data) => { if (live) setFonts(data.allFonts); }).catch(() => undefined);
    return () => { live = false; };
  }, []);

  const setColor = (key: ColorKey, hex: string) => {
    const norm = normalizeHex(hex) ?? hex.replace("#", "").toUpperCase();
    setDraft((d) => ({ ...d, colors: { ...d.colors, [key]: norm } }));
  };
  const setTitleFont = (name: string) => {
    setDraft((d) => ({ ...d, fonts: { ...d.fonts, titleFont: name }, titleFormat: { ...d.titleFormat, fontName: name } }));
  };
  // Body font cascades to subtitle/footnote too — brandApplier reads each
  // format's own fontName independently, and the mockup exposes only one
  // "Body" font control for all three.
  const setBodyFont = (name: string) => {
    setDraft((d) => ({
      ...d,
      fonts: { ...d.fonts, bodyFont: name },
      bodyTextFormat: { ...d.bodyTextFormat, fontName: name },
      subtitleFormat: { ...d.subtitleFormat, fontName: name },
      footnoteFormat: { ...d.footnoteFormat, fontName: name },
    }));
  };

  return (
    <div className={styles.wrapper}>
      <div className={styles.scrollArea}>
        <ModuleHeader title="Review & Customise" subtitle="Deckorate" onBack={onCancel} />
        <p className={styles.reviewIntro}>
          Everything below is ready to apply. Open a section only if you want to change something.
        </p>

        <details className={styles.reviewSection} open>
          <summary className={styles.reviewSummary}>Colours</summary>
          <div className={styles.colorGrid}>
            {COLOR_FIELDS.map(({ key, label }) => (
              <label key={key} className={styles.colorField}>
                <input
                  type="color"
                  className={styles.colorGridInput}
                  value={toColorInputValue(draft.colors[key])}
                  aria-label={label}
                  onChange={(e) => setColor(key, e.target.value)}
                />
                <span>{label}</span>
              </label>
            ))}
          </div>
        </details>

        <details className={styles.reviewSection}>
          <summary className={styles.reviewSummary}>Fonts</summary>
          <div className={styles.reviewSectionBody}>
            <FontField id="rc-title-font" label="Title" value={draft.fonts.titleFont} fonts={fonts} onChange={setTitleFont} />
            <FontField id="rc-body-font" label="Body" value={draft.fonts.bodyFont} fonts={fonts} onChange={setBodyFont} />
            <p className={styles.reviewHint}>Pick from installed fonts, the same list PowerPoint offers.</p>
          </div>
        </details>

        <details className={styles.reviewSection}>
          <summary className={styles.reviewSummary}>Title Format</summary>
          <div className={styles.reviewSectionBody}>
            <SizeField label="Size" value={draft.titleFormat.fontSize} onChange={(v) => setDraft((d) => ({ ...d, titleFormat: { ...d.titleFormat, fontSize: v } }))} />
            <BoolField label="Bold" checked={draft.titleFormat.bold ?? false} onChange={(v) => setDraft((d) => ({ ...d, titleFormat: { ...d.titleFormat, bold: v } }))} />
            <ColorField label="Colour" value={draft.titleFormat.fontColor} onChange={(hex) => setDraft((d) => ({ ...d, titleFormat: { ...d.titleFormat, fontColor: hex } }))} />
            <ColorField label="Highlight" value={draft.highlightColor} onChange={(hex) => setDraft((d) => ({ ...d, highlightColor: hex }))} />
            <p className={styles.reviewHint}>Highlight colour is applied to originally-coloured headings.</p>
          </div>
        </details>

        <details className={styles.reviewSection}>
          <summary className={styles.reviewSummary}>Body Text Format</summary>
          <div className={styles.reviewSectionBody}>
            <SizeField label="Size" value={draft.bodyTextFormat.fontSize} onChange={(v) => setDraft((d) => ({ ...d, bodyTextFormat: { ...d.bodyTextFormat, fontSize: v } }))} />
            <ColorField label="Colour" value={draft.bodyTextFormat.fontColor} onChange={(hex) => setDraft((d) => ({ ...d, bodyTextFormat: { ...d.bodyTextFormat, fontColor: hex } }))} />
          </div>
        </details>

        <details className={styles.reviewSection}>
          <summary className={styles.reviewSummary}>Subtitle Format</summary>
          <div className={styles.reviewSectionBody}>
            <SizeField label="Size" value={draft.subtitleFormat.fontSize} onChange={(v) => setDraft((d) => ({ ...d, subtitleFormat: { ...d.subtitleFormat, fontSize: v } }))} />
            <BoolField label="Bold" checked={draft.subtitleFormat.bold ?? false} onChange={(v) => setDraft((d) => ({ ...d, subtitleFormat: { ...d.subtitleFormat, bold: v } }))} />
            <ColorField label="Colour" value={draft.subtitleFormat.fontColor} onChange={(hex) => setDraft((d) => ({ ...d, subtitleFormat: { ...d.subtitleFormat, fontColor: hex } }))} />
          </div>
        </details>

        <details className={styles.reviewSection}>
          <summary className={styles.reviewSummary}>Footnote Format</summary>
          <div className={styles.reviewSectionBody}>
            <SizeField label="Size" value={draft.footnoteFormat.fontSize} onChange={(v) => setDraft((d) => ({ ...d, footnoteFormat: { ...d.footnoteFormat, fontSize: v } }))} />
            <ColorField label="Colour" value={draft.footnoteFormat.fontColor} onChange={(hex) => setDraft((d) => ({ ...d, footnoteFormat: { ...d.footnoteFormat, fontColor: hex } }))} />
          </div>
        </details>

        <details className={styles.reviewSection}>
          <summary className={styles.reviewSummary}>Table Format</summary>
          <div className={styles.reviewSectionBody}>
            <BoolField label="Bold" checked={draft.tableFormat.headerRow.bold} onChange={(v) => setDraft((d) => ({ ...d, tableFormat: { ...d.tableFormat, headerRow: { ...d.tableFormat.headerRow, bold: v } } }))} />
            <ColorField label="Header fill" value={draft.tableFormat.headerRow.fillColor} onChange={(hex) => setDraft((d) => ({ ...d, tableFormat: { ...d.tableFormat, headerRow: { ...d.tableFormat.headerRow, fillColor: hex } } }))} />
            <ColorField label="Header text" value={draft.tableFormat.headerRow.fontColor} onChange={(hex) => setDraft((d) => ({ ...d, tableFormat: { ...d.tableFormat, headerRow: { ...d.tableFormat.headerRow, fontColor: hex } } }))} />
          </div>
        </details>

        <details className={styles.reviewSection}>
          <summary className={styles.reviewSummary}>Advanced Options</summary>
          <div className={styles.reviewSectionBody}>
            <AdvancedOptionsFields advanced={draftAdv} onChange={setDraftAdv} />
          </div>
        </details>
      </div>

      <div className={styles.actionBar}>
        <button type="button" className={styles.applyBtn} onClick={() => onDone(draft, draftAdv)}>
          Done
        </button>
      </div>
    </div>
  );
}

function FontField({ id, label, value, fonts, onChange }: Readonly<{
  id: string; label: string; value: string; fonts: FontOption[]; onChange: (name: string) => void;
}>) {
  return (
    <div className={styles.reviewFieldRow}>
      <label htmlFor={id} className={styles.reviewFieldLabel}>{label}</label>
      <input
        id={id}
        type="text"
        list={`${id}-list`}
        className={styles.reviewTextInput}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
      <datalist id={`${id}-list`}>
        {fonts.map((f) => <option key={f.name} value={f.name} label={f.tag ? `${f.name} [${f.tag}]` : undefined} />)}
      </datalist>
    </div>
  );
}

function SizeField({ label, value, onChange }: Readonly<{ label: string; value: number; onChange: (v: number) => void }>) {
  return (
    <label className={styles.reviewFieldRow}>
      <span className={styles.reviewFieldLabel}>{label}</span>
      <input
        type="number"
        min={6}
        max={96}
        className={styles.reviewNumberInput}
        value={value}
        onChange={(e) => {
          const n = Number.parseInt(e.target.value, 10);
          if (!Number.isNaN(n)) onChange(n);
        }}
      />
    </label>
  );
}

function BoolField({ label, checked, onChange }: Readonly<{ label: string; checked: boolean; onChange: (v: boolean) => void }>) {
  return (
    <label className={styles.reviewCheckField}>
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
      <span>{label}</span>
    </label>
  );
}

function ColorField({ label, value, onChange }: Readonly<{ label: string; value: string; onChange: (hex: string) => void }>) {
  return (
    <label className={styles.reviewFieldRow}>
      <span className={styles.reviewFieldLabel}>{label}</span>
      <input
        type="color"
        className={styles.reviewColorInput}
        value={toColorInputValue(value)}
        onChange={(e) => onChange(normalizeHex(e.target.value) ?? e.target.value.replace("#", "").toUpperCase())}
      />
    </label>
  );
}
